import { useState, useRef, useEffect, memo, ImgHTMLAttributes } from "react";

/**
 * Transforms image URLs to request WebP format and specific widths.
 * Supports: Unsplash, Supabase Storage, and generic URLs.
 */
function buildSrcSet(src: string, widths: number[]): string {
  return widths
    .map((w) => {
      const url = getResizedUrl(src, w);
      return url ? `${url} ${w}w` : null;
    })
    .filter(Boolean)
    .join(", ");
}

function getResizedUrl(src: string, width: number): string | null {
  if (!src) return null;

  // Unsplash
  if (src.includes("unsplash.com")) {
    const url = new URL(src);
    url.searchParams.set("w", String(width));
    url.searchParams.set("q", "80");
    url.searchParams.set("fm", "webp");
    url.searchParams.set("auto", "format");
    url.searchParams.set("fit", "crop");
    return url.toString();
  }

  // Supabase Storage — use image transformation
  if (src.includes("supabase.co/storage")) {
    const separator = src.includes("?") ? "&" : "?";
    return `${src}${separator}width=${width}&format=webp&quality=80`;
  }

  // CDN URLs that accept width params (Apple, Samsung, etc.) — return as-is
  return src;
}

function getOptimizedSrc(src: string): string {
  if (!src) return src;
  if (src.includes("unsplash.com")) {
    const url = new URL(src);
    url.searchParams.set("fm", "webp");
    url.searchParams.set("q", "80");
    url.searchParams.set("auto", "format");
    return url.toString();
  }
  return src;
}

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src: string;
  alt: string;
  /** Responsive widths for srcset generation (default: [320, 640, 960, 1280]) */
  widths?: number[];
  /** CSS sizes attribute (default: "100vw") */
  sizes?: string;
  /** Eager loading for above-fold images */
  priority?: boolean;
  /** Aspect ratio for placeholder (e.g. "4/3", "1/1", "16/9") */
  aspectRatio?: string;
}

const OptimizedImage = memo(({
  src,
  alt,
  widths = [320, 640, 960, 1280],
  sizes = "100vw",
  priority = false,
  aspectRatio,
  className = "",
  style,
  ...rest
}: OptimizedImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(priority);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection observer for true lazy loading
  useEffect(() => {
    if (priority || inView) return;
    const el = imgRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [priority, inView]);

  const optimizedSrc = getOptimizedSrc(src);
  const srcSet = buildSrcSet(src, widths);

  return (
    <img
      ref={imgRef}
      src={inView ? optimizedSrc : undefined}
      srcSet={inView && srcSet ? srcSet : undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      fetchPriority={priority ? "high" : undefined}
      onLoad={() => setLoaded(true)}
      className={`${className} transition-opacity duration-300 ${loaded || !inView ? "opacity-100" : "opacity-0"}`}
      style={{
        ...style,
        ...(aspectRatio && !loaded && !inView ? { aspectRatio } : {}),
      }}
      {...rest}
    />
  );
});

OptimizedImage.displayName = "OptimizedImage";

export default OptimizedImage;
export { getOptimizedSrc, getResizedUrl, buildSrcSet };
